import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import {
  fetchPublishedPosts,
  fetchPublishedPostBySlug,
  type BlogPostWithTags,
} from "./blog.server";

// ---------- Public ----------

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({ tag: z.string().nullish() })
      .default({})
      .parse(d ?? {})
  )
  .handler(async ({ data }): Promise<BlogPostWithTags[]> => {
    const posts = await fetchPublishedPosts({ tagSlug: data.tag ?? null });
    return posts;
  });

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }): Promise<BlogPostWithTags | null> => {
    const post = await fetchPublishedPostBySlug(data.slug);
    return post;
  });

// ---------- Admin: requires admin role ----------

async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Response("Forbidden", { status: 403 });
}

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    return { isAdmin: !!data };
  });

export const adminListAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select("id, slug, title, status, published_at, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const adminGetPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*, blog_post_tags(tag_id, tag:blog_tags(id, slug, name))")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return post;
  });

export const adminListTags = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("blog_tags")
      .select("id, slug, name")
      .order("name");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const upsertSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be lowercase letters, numbers, and dashes"),
  title: z.string().min(1).max(200),
  excerpt: z.string().max(500).default(""),
  content: z.string().default(""),
  cover_image_url: z.string().url().nullable().optional(),
  cover_image_alt: z.string().max(200).nullable().optional(),
  status: z.enum(["draft", "published"]),
  published_at: z.string().datetime().nullable().optional(),
  author_name: z.string().min(1).max(100).default("Three Day Launch"),
  meta_title: z.string().max(60).nullable().optional(),
  meta_description: z.string().max(160).nullable().optional(),
  tag_names: z.array(z.string().min(1).max(50)).default([]),
});

export const adminUpsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => upsertSchema.parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);

    // If publishing without a published_at, set it now
    const published_at =
      data.status === "published"
        ? data.published_at ?? new Date().toISOString()
        : data.published_at ?? null;

    // Require alt text when publishing with a cover image
    if (data.status === "published" && data.cover_image_url && !data.cover_image_alt) {
      throw new Response("Cover image alt text is required to publish.", { status: 400 });
    }

    const payload = {
      slug: data.slug,
      title: data.title,
      excerpt: data.excerpt,
      content: data.content,
      cover_image_url: data.cover_image_url ?? null,
      cover_image_alt: data.cover_image_alt ?? null,
      status: data.status,
      published_at,
      author_name: data.author_name,
      meta_title: data.meta_title ?? null,
      meta_description: data.meta_description ?? null,
    };

    let postId = data.id;
    if (postId) {
      const { error } = await supabaseAdmin
        .from("blog_posts")
        .update(payload)
        .eq("id", postId);
      if (error) throw new Error(error.message);
    } else {
      const { data: inserted, error } = await supabaseAdmin
        .from("blog_posts")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      postId = inserted.id;
    }

    // Tags: upsert by slug, then sync junction
    const tagSlugs = Array.from(
      new Set(
        data.tag_names
          .map((n) => n.trim())
          .filter(Boolean)
          .map((n) => ({
            name: n,
            slug: n
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, ""),
          }))
          .filter((t) => t.slug.length > 0)
          .map((t) => JSON.stringify(t))
      )
    ).map((s) => JSON.parse(s) as { name: string; slug: string });

    let tagIds: string[] = [];
    if (tagSlugs.length > 0) {
      const { error: tagErr } = await supabaseAdmin
        .from("blog_tags")
        .upsert(tagSlugs, { onConflict: "slug" });
      if (tagErr) throw new Error(tagErr.message);

      const { data: tagRows, error: tagSelErr } = await supabaseAdmin
        .from("blog_tags")
        .select("id, slug")
        .in(
          "slug",
          tagSlugs.map((t) => t.slug)
        );
      if (tagSelErr) throw new Error(tagSelErr.message);
      tagIds = (tagRows ?? []).map((r) => r.id);
    }

    // Replace junctions for this post
    const { error: delErr } = await supabaseAdmin
      .from("blog_post_tags")
      .delete()
      .eq("post_id", postId!);
    if (delErr) throw new Error(delErr.message);

    if (tagIds.length > 0) {
      const { error: insErr } = await supabaseAdmin
        .from("blog_post_tags")
        .insert(tagIds.map((tid) => ({ post_id: postId!, tag_id: tid })));
      if (insErr) throw new Error(insErr.message);
    }

    return { id: postId };
  });

export const adminDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
